import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Camera, CameraOff, ScanLine, X } from "lucide-react";

export function parsePaletteCode(text) {
  const m = String(text || "").toUpperCase().match(/AUT(-D)?-\d{4}-\d{6}/);
  return m ? m[0] : null;
}

export function feedback(ok) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.frequency.value = ok ? 1400 : 300;
    g.gain.value = 0.15;
    o.start();
    o.stop(ctx.currentTime + (ok ? 0.12 : 0.35));
  } catch (_) { /* audio indisponible */ }
  if (navigator.vibrate) navigator.vibrate(ok ? 60 : [120, 60, 120]);
}

export default function ScanPalette({ onCode, onClose, lastResult, hint, count, countLabel, total }) {
  const [manual, setManual] = useState("");
  const [camState, setCamState] = useState("starting"); // starting | on | off
  const inputRef = useRef(null);
  const qrRef = useRef(null);
  const busyRef = useRef(false);

  useEffect(() => {
    const el = document.getElementById("qr-reader");
    if (!el) return undefined;
    const qr = new Html5Qrcode("qr-reader", { verbose: false });
    qrRef.current = qr;
    qr.start({ facingMode: "environment" }, { fps: 8, qrbox: { width: 220, height: 220 } },
      (text) => {
        if (busyRef.current) return;
        busyRef.current = true;
        Promise.resolve(onCode(text)).finally(() => setTimeout(() => { busyRef.current = false; }, 1200));
      }, () => {})
      .then(() => setCamState("on"))
      .catch(() => setCamState("off"));
    return () => {
      const q = qrRef.current;
      if (q && q.isScanning) q.stop().then(() => q.clear()).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
    const keep = setInterval(() => {
      if (document.activeElement === document.body) inputRef.current?.focus();
    }, 800);
    return () => clearInterval(keep);
  }, []);

  const submitManual = (e) => {
    e.preventDefault();
    if (!manual.trim()) return;
    onCode(manual);
    setManual("");
  };

  return (
    <div data-testid="scan-panel" className="rounded-2xl border-4 border-slate-900 bg-slate-950 text-white p-4 sm:p-5 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 font-heading font-black uppercase tracking-wide text-lg">
            <ScanLine className="w-6 h-6 text-emerald-400 animate-pulse" /> Mode scan actif
          </div>
          {count !== undefined && (
            <div data-testid="scan-counter" className="flex items-baseline gap-2 rounded-xl bg-emerald-500/15 border border-emerald-400/40 px-3 py-1">
              <span className="font-mono font-black text-3xl text-emerald-300 leading-none">{count}{total !== undefined && <span className="text-lg text-emerald-200/70"> / {total}</span>}</span>
              <span className="text-[10px] uppercase tracking-widest text-emerald-200 font-bold">{countLabel || "scannées"}</span>
            </div>
          )}
        </div>
        <Button data-testid="scan-close-btn" variant="ghost" onClick={onClose} className="text-white hover:bg-white/10 h-10 font-bold">
          <X className="w-5 h-5 mr-1" /> Terminer
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl overflow-hidden bg-black relative min-h-[220px]">
          <div id="qr-reader" className="w-full" />
          {camState !== "on" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4 text-slate-300 text-sm">
              {camState === "starting" ? <Camera className="w-8 h-8 mb-2 animate-pulse" /> : <CameraOff className="w-8 h-8 mb-2 opacity-60" />}
              {camState === "starting" ? "Ouverture de la caméra…" : "Caméra indisponible — utilisez la scanette ou la saisie ci-contre."}
            </div>
          )}
        </div>
        <div className="space-y-3">
          <form onSubmit={submitManual}>
            <label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Scanette / n° palette</label>
            <Input
              ref={inputRef}
              data-testid="scan-manual-input"
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              placeholder="AUT-2026-000001 puis Entrée"
              autoComplete="off"
              className="mt-1 h-14 text-lg font-mono bg-white text-slate-900"
            />
          </form>
          <p className="text-xs text-slate-400">
            {hint || "Chaque palette scannée (caméra ou douchette) est ajoutée automatiquement au bon de livraison. Scannez toutes les palettes, puis validez le BL."}
          </p>
          {lastResult && (
            <div data-testid="scan-last-result" className={`rounded-lg px-3 py-2 font-bold text-sm ${lastResult.ok ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"}`}>
              {lastResult.message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
